import { colors } from '../styles/tokens';

/**
 * Format a date string to localized date and time
 * @param {string} dateString - The date string to format
 * @param {Object} options - Formatting options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }).format(date);
};

/**
 * Format a date as smart relative/contextual time.
 * - < 1min: "A l'instant"
 * - < 1h: "Il y a X min"
 * - < ~2h (same day): "Il y a Xh"
 * - Yesterday: "Hier à 18h45"
 * - Same month: "Le 4 à 0h12"
 * - Same year: "Le 6 février à 12h40"
 * - Older: "Le 10 décembre 2025 à 14h30"
 * @param {string} dateString
 * @returns {string}
 */
export const formatRelativeDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';

    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffH = Math.floor(diffMin / 60);

    const timeStr = `${date.getHours()}h${date.getMinutes().toString().padStart(2, '0')}`;

    if (diffMin < 1) return "à l'instant";
    if (diffMin < 60) return `il y a ${diffMin} min`;
    if (diffH < 2 && date.getDate() === now.getDate()) return `il y a ${diffH}h`;

    const isYesterday = (() => {
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        return date.getDate() === yesterday.getDate()
            && date.getMonth() === yesterday.getMonth()
            && date.getFullYear() === yesterday.getFullYear();
    })();

    if (isYesterday) return `hier à ${timeStr}`;

    const sameYear = date.getFullYear() === now.getFullYear();
    const sameMonth = sameYear && date.getMonth() === now.getMonth();
    const monthName = date.toLocaleDateString('fr-FR', { month: 'long' });

    if (sameMonth) return `le ${date.getDate()} à ${timeStr}`;
    if (sameYear) return `le ${date.getDate()} ${monthName} à ${timeStr}`;
    return `le ${date.getDate()} ${monthName} ${date.getFullYear()} à ${timeStr}`;
};

/**
 * Formats phone number to standard format
 * @param {string} phone - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phone) => {
    if (!phone) return '';

    // For French numbers: transform +33x to 0x format
    let formatted = phone;
    if (phone.startsWith('+33')) {
        formatted = '0' + phone.substring(3);
    } else if (phone.startsWith('33') && !phone.startsWith('+')) {
        formatted = '0' + phone.substring(2);
    }

    // Remove all non-digit characters
    formatted = formatted.replace(/\D/g, '');

    // Add spaces every 2 digits for French format (0x xx xx xx xx)
    if (formatted.length === 10 && formatted.startsWith('0')) {
        return formatted.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
    }

    return phone; // Return original if not in expected format
};

/**
 * Calculate and format call duration
 * @param {string} startDate - Call start date
 * @param {string} endDate - Call end date
 * @returns {string} Formatted duration string
 */
export const getDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return '';

    const start = new Date(startDate);
    const end = new Date(endDate);
    const durationMs = end.getTime() - start.getTime();
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);

    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

/**
 * Format call duration in minutes and seconds
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration
 */
export const formatDuration = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0 min';

    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);

    return `${minutes} min ${secs} sec`;
};

/**
 * Get confidence score label and color based on score value
 * @param {number|null} score - Confidence score (0-100)
 * @returns {Object} Object with label, color, and backgroundColor
 */
export const getConfidenceScoreConfig = (score) => {
    if (score === null || score === undefined) {
        return {
            label: 'N/A',
            color: '#666',
            backgroundColor: '#f5f5f5',
            chipColor: 'default'
        };
    }

    if (score >= 85) {
        return {
            label: 'Excellent',
            color: colors.green.dark,
            backgroundColor: '#e8f5e8',
            chipColor: 'success'
        };
    } else if (score >= 70) {
        return {
            label: 'Bon',
            color: '#ed6c02',
            backgroundColor: '#fff8e1',
            chipColor: 'warning'
        };
    } else if (score >= 50) {
        return {
            label: 'Moyen',
            color: colors.orange.main,
            backgroundColor: '#fff3e0',
            chipColor: 'warning'
        };
    } else {
        return {
            label: 'Faible',
            color: colors.red.main,
            backgroundColor: '#ffeef0',
            chipColor: 'error'
        };
    }
};